<template>
  <div class="csp" v-click-outside="close">
    <button
      type="button"
      class="csp-trigger"
      :class="{ empty: !modelValue, compact }"
      :style="modelValue ? { background: modelValue } : null"
      :title="title || ''"
      @click.stop="open = !open"
    >
      <svg v-if="!modelValue" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="13.5" cy="6.5" r=".5"/><circle cx="17.5" cy="10.5" r=".5"/><circle cx="8.5" cy="7.5" r=".5"/><circle cx="6.5" cy="12.5" r=".5"/><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c1.7 0 3-1.3 3-3 0-.8-.3-1.5-.8-2-.5-.6-.8-1.2-.8-2 0-1.7 1.3-3 3-3h1.8c2.7 0 4.8-2.1 4.8-4.8C22 5.5 17.5 2 12 2z"/></svg>
    </button>

    <div v-if="open" class="csp-pop" @click.stop>
      <div class="csp-grid">
        <button
          v-for="c in palette"
          :key="c"
          class="sw"
          :class="{ active: (modelValue || '').toLowerCase() === c }"
          :style="{ background: c }"
          :aria-label="c"
          @click="pick(c)"
        ><span v-if="(modelValue || '').toLowerCase() === c" class="tick">✓</span></button>
      </div>
      <div class="csp-foot">
        <input
          type="color"
          class="csp-native"
          :value="modelValue || '#59CE7F'"
          @input="pick($event.target.value)"
        />
        <button v-if="modelValue" class="csp-clear" @click="pick(null)">{{ clearLabel }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import { useI18n } from '@/composables/useI18n'

// Curated Monday-ish palette — soft, saturated enough to read at a glance.
const PALETTE = [
  '#59CE7F', '#3EB9A6', '#7EA6F0', '#9F84E8', '#F27D9A',
  '#F5A25D', '#E6B855', '#B591D6', '#6B93D6', '#D66B6B',
  '#8C99A8', '#2E9E5B',
]

// Cheap "click outside" directive that doesn't drag in a util package. Attached
// per component instance; unmounts cleanly with the popover.
const clickOutside = {
  beforeMount(el, binding) {
    el.__cspClickOutside = (e) => {
      if (!(el === e.target || el.contains(e.target))) binding.value?.(e)
    }
    document.addEventListener('mousedown', el.__cspClickOutside, true)
  },
  unmounted(el) {
    document.removeEventListener('mousedown', el.__cspClickOutside, true)
    delete el.__cspClickOutside
  },
}

export default {
  name: 'ColorSwatchPicker',
  props: {
    modelValue: { type: String, default: null },
    compact:    { type: Boolean, default: false },
    title:      { type: String, default: '' },
  },
  emits: ['update:modelValue'],
  directives: { 'click-outside': clickOutside },
  setup(props, { emit }) {
    const { t } = useI18n()
    const open = ref(false)
    const palette = PALETTE

    function pick(hex) {
      if (hex && typeof hex === 'string') hex = hex.toLowerCase()
      emit('update:modelValue', hex || null)
      if (hex === null) open.value = false
    }
    function close() { open.value = false }

    return { open, palette, pick, close, clearLabel: t('fields.clearColor') }
  },
}
</script>

<style scoped>
.csp { position: relative; display: inline-flex; }
.csp-trigger {
  width: 26px; height: 26px; border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface-2);
  cursor: pointer; padding: 0;
  display: inline-grid; place-items: center;
  color: var(--muted);
  box-shadow: inset 0 0 0 1px rgba(0,0,0,.04);
  transition: transform .12s;
}
.csp-trigger:hover { transform: translateY(-1px); }
.csp-trigger.compact { width: 22px; height: 22px; border-radius: 7px; }
.csp-trigger.empty { background: var(--surface-2); }

.csp-pop {
  position: absolute; top: calc(100% + 6px); inset-inline-start: 0;
  z-index: 1400;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: 0 12px 28px rgba(16,22,40,.16);
  padding: 10px;
  min-width: 176px;
}
.csp-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 6px; }
.sw {
  width: 22px; height: 22px; border: none; border-radius: 6px; cursor: pointer;
  box-shadow: 0 0 0 1px rgba(0,0,0,.06);
  display: inline-grid; place-items: center;
  transition: transform .12s;
}
.sw:hover { transform: scale(1.08); }
.sw.active { box-shadow: 0 0 0 2px var(--surface), 0 0 0 4px var(--ink, #222); }
.tick { color: #fff; font-weight: 800; font-size: 12px; text-shadow: 0 1px 2px rgba(0,0,0,.35); }

.csp-foot { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 8px; }
.csp-native { width: 32px; height: 26px; padding: 0; border: none; background: none; cursor: pointer; border-radius: 6px; }
.csp-clear { border: none; background: none; color: var(--muted); font-size: 12px; cursor: pointer; padding: 3px 6px; border-radius: 6px; }
.csp-clear:hover { color: var(--text); background: var(--hover); }
</style>
